/**
 * The Team tab's sub-views (plan section 13 tab 6), in SegmentedTabs order.
 * TeamTab renders the entry whose path is the first URL segment after
 * /lms/team (the first entry when there is none).
 *
 * Hot file (plan 15.12): WP-03 only. A sub-view WP replaces the ONE file its
 * entry imports (keeping that file's default export); it never edits this
 * registry, TeamTab.tsx or tabs/config.ts.
 */
import { BadgeCheck, BarChart3, ClipboardCheck, Contact, FileBadge, Grid3x3, ListChecks } from 'lucide-react';
import type { LmsSubview } from '../config';
import MatrixView from './matrix/MatrixView';
import PeopleView from './people/PeopleView';
import SignoffsView from './signoffs/SignoffsView';
import ExternalCertsView from './externalCerts/ExternalCertsView';
import OnboardingRunsView from './onboarding/OnboardingRunsView';
import ReportsView from './reports/ReportsView';
import VerifyView from './verify/VerifyView';

// Descriptions are double-quoted on purpose (the apostrophe hazard, common.md).
export const TEAM_SUBVIEWS: LmsSubview[] = [
  {
    path: 'matrix',
    label: 'Matrix',
    // plan 8.4, R16, R22
    description: "Who is compliant, expiring, overdue or missing for each required course, by site and group, with waivers shown.",
    icon: Grid3x3,
    Component: MatrixView,
    ownerWp: 'WP-45',
  },
  {
    path: 'people',
    label: 'People',
    // plan section 13 tab 6
    description: "The directory, and a page per person: transcript, assignments, waivers, sign-offs, external certificates, onboarding runs and the training log.",
    icon: Contact,
    Component: PeopleView,
    ownerWp: 'WP-45',
  },
  {
    path: 'signoffs',
    label: 'Sign-offs',
    // plan 7.4, R18
    description: "Competency sign-off requests waiting for an observer, and the skills checklist to record.",
    icon: ClipboardCheck,
    Component: SignoffsView,
    ownerWp: 'WP-45',
  },
  {
    path: 'external-certificates',
    label: 'External certificates',
    // plan 7.5, R17
    description: "Certificates people earned elsewhere and uploaded, waiting to be accepted or rejected.",
    icon: FileBadge,
    Component: ExternalCertsView,
    ownerWp: 'WP-45',
  },
  {
    path: 'onboarding',
    label: 'Onboarding',
    // plan 12.2, 12.3, R14
    description: "Start an onboarding checklist for a new team member, and work and verify the items of each run.",
    icon: ListChecks,
    Component: OnboardingRunsView,
    ownerWp: 'WP-47',
  },
  {
    path: 'reports',
    label: 'Reports',
    // plan section 13 tab 6, R16, R21
    description: "Completion rates per course and site, item analysis per course version, campaign outcomes and CSV exports.",
    icon: BarChart3,
    Component: ReportsView,
    ownerWp: 'WP-45',
  },
  {
    path: 'verify',
    label: 'Verify a certificate',
    // plan 10.4, R24
    description: "Look up a certificate by its code and see whether it is valid, expired or superseded.",
    icon: BadgeCheck,
    Component: VerifyView,
    ownerWp: 'WP-45',
  },
];
